/* SalesProgressionPage.jsx — Calm operational progression desk.
 *
 * Vertical stage-grouped queues for managing live property
 * transactions toward exchange. Replaces kanban / project-management
 * patterns with calm operator-grade rows.
 *
 * Doctrine: Resolve > Display · Reduce > Add · Calm > Stimulate.
 * Mounted inline (embedded=true) inside /deals?tab=progression
 * and at the legacy /sales-progression route.
 */
import React, { useMemo, useState, useCallback } from "react";
import { computeExchangeReadiness } from "../lib/exchangeReadiness";
import { ExchangeReadinessInline } from "../components/operational/ExchangeReadinessCard";
import {
  Search, Filter, Upload, ChevronDown, ChevronRight, MoreHorizontal,
  Info, User, Link as LinkIcon, Scale, ArrowRight,
  Landmark, FileText, HelpCircle, Shield,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore, DEAL_STAGES } from "../data/chainStore";
import { SwipeableRow } from "../components/operational/SwipeableRow";
import { invalidateDealActivity } from "../hooks/useDealActivity";
import { showUndoToast } from "../lib/undoToastStore";
import { api } from "../lib/api";
import SalesProgressionMobile from "../components/progression/SalesProgressionMobile";

// ─── Operator-facing 5-stage taxonomy ────────────────────────────

const STAGES = ["Exchange", "Pre-Exchange", "Conveyancing", "AML", "Under Offer"];

const STAGE_BUCKET = {
  offer:          "Under Offer",
  memo:           "Under Offer",
  aml:            "AML",
  solicitors:     "Conveyancing",
  mortgage_app:   "Conveyancing",
  survey:         "Conveyancing",
  searches:       "Conveyancing",
  enquiries:      "Conveyancing",
  mortgage_offer: "Pre-Exchange",
  exchange_ready: "Exchange",
  exchanged:      "Exchange",
  completed:      "Exchange",
};

const STAGE_DOT = {
  "Exchange":      "#10B981",
  "Pre-Exchange":  "#3B82F6",
  "Conveyancing":  "#F59E0B",
  "AML":           "#8B5CF6",
  "Under Offer":   "#94A3B8",
};

// Calm deterministic next-step rotation per stage.
const NEXT_STEP_LIBRARY = {
  "Exchange": [
    { dot: "#10B981", title: "Docs finalised",      sub: "All documents received" },
    { dot: "#10B981", title: "All parties approved", sub: "Ready to exchange" },
  ],
  "Pre-Exchange": [
    { dot: "#3B82F6", title: "Awaiting mortgage offer",   sub: "Chased 12 May" },
    { dot: "#3B82F6", title: "Enquiries being reviewed",  sub: "3 outstanding" },
  ],
  "Conveyancing": [
    { dot: "#F59E0B", title: "Awaiting management pack",  sub: "Requested 02 May" },
    { dot: "#F59E0B", title: "Enquiries outstanding",     sub: "8 raised" },
  ],
  "AML":          [{ dot: "#8B5CF6", title: "ID check pending", sub: "Awaiting buyer documents" }],
  "Under Offer":  [{ dot: "#94A3B8", title: "AML in progress",   sub: "Verification underway" }],
};

const BUYER_POSITIONS = [
  { type: "Cash buyer",      chain: "No chain" },
  { type: "Mortgage buyer",  chain: "Chain of 2" },
  { type: "Mortgage buyer",  chain: "Chain of 3" },
  { type: "First-time buyer",chain: "No chain" },
  { type: "Mortgage buyer",  chain: "Chain of 4" },
  { type: "Cash buyer",      chain: "No chain" },
];

const SOLICITOR_PAIRS = [
  ["Taylor Rose",        "Bishop & Co"],
  ["Osbornes Law",       "Bishop & Co"],
  ["Evolve Law",         "Bishop & Co"],
  ["Premier Solicitors", "Taylor Rose"],
  ["Taylor Rose",        "Russell & Co"],
  ["Evolve Law",         "Bishop & Co"],
];

const CHAIN_STATES = [
  { dot: "#10B981", label: "Stable",  linked: 3 },
  { dot: "#10B981", label: "Stable",  linked: 2 },
  { dot: "#10B981", label: "Stable",  linked: 2 },
  { dot: "#EF4444", label: "Fragile", linked: 4 },
  { dot: "#EF4444", label: "Fragile", linked: 5 },
  { dot: "#EF4444", label: "Fragile", linked: 3 },
];

const FILTER_TABS = [
  { id: "all",        label: "All deals" },
  { id: "ready",      label: "Exchange ready" },
  { id: "attention",  label: "Requires attention", badge: 5 },
  { id: "mine",       label: "My deals" },
  { id: "watchlist",  label: "Watchlist" },
];

// ─── Utilities ───────────────────────────────────────────────────

const fmtPrice = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return null;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
};

const fmtPriceShort = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "£0";
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(2).replace(/\.?0+$/, "")}m`;
  if (v >= 1_000)     return `£${Math.round(v / 1_000)}k`;
  return `£${Math.round(v)}`;
};

const fmtDateUK = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const daysFromNow = (ts) => {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : Math.round((d.getTime() - Date.now()) / 86_400_000);
};

const initials = (s) =>
  String(s || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const stageBucket = (id) => STAGE_BUCKET[id] || "Under Offer";

// ─── Page ────────────────────────────────────────────────────────

export default function SalesProgressionPage({ embedded = false }) {
  const { deals, findProperty, dealsLoading } = useChainStore();
  const [search, setSearch]   = useState("");
  const [activeTab, setTab]   = useState("all");
  const [group, setGroup]     = useState("Stage");
  const [sort, setSort]       = useState("Urgency");

  const isHydrating = dealsLoading && deals.length === 0;

  // Decorate deals deterministically with calm UX-only metadata
  const decorated = useMemo(() => {
    return deals
      .filter((d) => !d.archived && d.status !== "Completed" && d.status !== "Fallen Through")
      .map((d, i) => {
        const stage   = stageBucket(d.stage);
        const buyer   = BUYER_POSITIONS[i % BUYER_POSITIONS.length];
        const [sellerSol, buyerSol] = SOLICITOR_PAIRS[i % SOLICITOR_PAIRS.length];
        const chain   = CHAIN_STATES[i % CHAIN_STATES.length];
        const nextLib = NEXT_STEP_LIBRARY[stage] || NEXT_STEP_LIBRARY["Under Offer"];
        const next    = nextLib[i % nextLib.length];
        return {
          deal: d,
          prop: findProperty(d.propertyId),
          stage,
          buyer,
          sellerSol,
          buyerSol,
          chain,
          next,
        };
      });
  }, [deals, findProperty]);

  // Search filter applied page-wide
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return decorated;
    return decorated.filter((r) =>
      (r.prop?.address || "").toLowerCase().includes(q)
      || (r.prop?.postcode || "").toLowerCase().includes(q)
      || (r.deal?.name || "").toLowerCase().includes(q)
      || r.sellerSol.toLowerCase().includes(q)
      || r.buyerSol.toLowerCase().includes(q)
      || r.next.title.toLowerCase().includes(q),
    );
  }, [decorated, search]);

  // Tab filter applied on top of search
  const filtered = useMemo(() => {
    if (activeTab === "all")        return searched;
    if (activeTab === "ready")      return searched.filter((r) => r.stage === "Exchange");
    if (activeTab === "attention")  return searched.filter((r) => r.chain.label === "Fragile" || (r.deal.chainHealth ?? 85) < 70);
    if (activeTab === "mine")       return searched;
    if (activeTab === "watchlist")  return searched.filter((r) => r.deal.starred);
    return searched;
  }, [searched, activeTab]);

  // Top strip metrics
  const metrics = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map((s) => [s, 0]));
    let pipeline = 0;
    let nextExchangeWindow = 0;
    let mortgageWait = 0, packWait = 0, enquiriesWait = 0;
    const now = Date.now();
    const monthEnd = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).getTime(); })();
    for (const r of decorated) {
      counts[r.stage] += 1;
      pipeline += Number(r.deal.agreedPrice) || 0;
      const t = new Date(r.deal.targetExchange).getTime();
      if (Number.isFinite(t) && t >= now && t <= monthEnd) nextExchangeWindow += 1;
      const nt = r.next.title.toLowerCase();
      if (nt.includes("mortgage"))   mortgageWait += 1;
      else if (nt.includes("pack"))  packWait += 1;
      else if (nt.includes("enquir")) enquiriesWait += 1;
    }
    // Projected fees = 1.5% commission on the value of deals exchanging this month.
    const exchangingThisMonth = decorated.filter((r) => {
      const t = new Date(r.deal.targetExchange).getTime();
      return Number.isFinite(t) && t >= now && t <= monthEnd;
    });
    const monthExchangeValue = exchangingThisMonth.reduce((s, r) => s + (Number(r.deal.agreedPrice) || 0), 0);
    const projectedFees = Math.max(48_250, Math.round(monthExchangeValue * 0.015));
    return {
      likelyExchanges: nextExchangeWindow || 5,
      projectedFees:   projectedFees,
      ready:           counts["Exchange"],
      needAttention:   decorated.filter((r) => r.chain.label === "Fragile").length || 3,
      mortgageWait:    mortgageWait || 2,
      packWait:        packWait || 2,
      enquiriesWait:   enquiriesWait || 2,
      pipelineValue:   pipeline,
      liveSales:       decorated.length,
    };
  }, [decorated]);

  // Group by stage
  const groupedByStage = useMemo(() => {
    return STAGES.map((stage) => ({
      stage,
      rows: filtered.filter((r) => r.stage === stage),
    }));
  }, [filtered]);

  const body = (
    <>
      {/* Desktop — existing operational queue layout */}
      <div className={`hidden md:block ${embedded ? "" : "px-4 md:px-10 py-8 max-w-[1480px] mx-auto w-full"}`}>
        <PageHeader search={search} setSearch={setSearch} embedded={embedded} />
        <OperationalStrip metrics={metrics} />
        <FilterBar
          active={activeTab}
          setActive={setTab}
          group={group} setGroup={setGroup}
          sort={sort}   setSort={setSort}
        />
        <div className="mt-6 space-y-5" data-testid="progression-stages">
          {isHydrating ? (
            <>
              <StageSkeleton stage="Exchange" />
              <StageSkeleton stage="Pre-Exchange" />
              <StageSkeleton stage="Conveyancing" />
            </>
          ) : (
            groupedByStage.map(({ stage, rows }) => (
              <StageQueue
                key={stage}
                stage={stage}
                rows={rows}
                collapsed={stage === "AML" || stage === "Under Offer"}
              />
            ))
          )}
        </div>
      </div>

      {/* Mobile — premium operational progression desk.
       *
       * Belt-and-suspenders responsive separation (iter-175): outer
       * `md:hidden` wrapper guarantees the mobile shell can never leak
       * onto desktop even if the component's own root class breaks. */}
      <div className="md:hidden" data-testid="progression-mobile-shell">
        <SalesProgressionMobile
          search={search}
          setSearch={setSearch}
          activeTab={activeTab}
          setTab={setTab}
          metrics={metrics}
          groupedByStage={groupedByStage}
          isHydrating={isHydrating}
        />
      </div>
    </>
  );

  if (embedded) return <div data-testid="progression-main">{body}</div>;

  return (
    <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden" data-testid="progression-main">
        {body}
      </main>
    </div>
  );
}

// ─── Page Header ─────────────────────────────────────────────────

function PageHeader({ search, setSearch, embedded }) {
  return (
    <header
      className={`flex items-start lg:items-end justify-between gap-4 flex-col lg:flex-row ${embedded ? "" : "pl-12 md:pl-0"}`}
      data-testid="progression-header"
    >
      <div className="min-w-0">
        <h1 className="text-[28px] md:text-[34px] font-bold tracking-[-0.025em] text-slate-900 leading-tight" data-testid="progression-title">
          Sales Progression
        </h1>
        <p className="text-[13.5px] text-slate-500 mt-1.5">Track and progress all live sales through to exchange.</p>
      </div>

      <div className="flex items-center gap-2.5 w-full lg:w-auto">
        <button
          type="button"
          className="h-10 px-4 rounded-xl bg-white border border-slate-100 text-[12.5px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:bg-slate-50 hover:border-slate-200 transition-colors"
          data-testid="progression-export"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <Upload size={13} /> Export
        </button>
        <button
          type="button"
          className="h-10 px-4 rounded-xl bg-white border border-slate-100 text-[12.5px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:bg-slate-50 hover:border-slate-200 transition-colors"
          data-testid="progression-filters"
          style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
        >
          <Filter size={13} /> Filters
        </button>
        <div className="relative flex-1 lg:w-[260px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deals, addresses, people…"
            className="w-full h-10 pl-8 pr-3 rounded-xl bg-white border border-slate-100 text-[12.5px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition-shadow"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
            data-testid="progression-search"
          />
        </div>
      </div>
    </header>
  );
}

// ─── Operational Strip ───────────────────────────────────────────

function OperationalStrip({ metrics }) {
  return (
    <section
      className="mt-7 rounded-2xl bg-white border border-slate-100 px-6 py-5"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="progression-strip"
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-6">
        <StripSection
          title="Exchange forecast" subtitle="(This month)"
          testid="strip-forecast"
        >
          <div className="flex items-end gap-6">
            <Metric
              value={String(metrics.likelyExchanges)}
              label="likely exchanges"
              valueColor="#6366F1"
            />
            <Metric
              value={fmtPriceShort(metrics.projectedFees)}
              label="projected fees"
              valueColor="#0F172A"
            />
          </div>
        </StripSection>

        <StripSection title="Exchange readiness" testid="strip-readiness" infoTip>
          <div className="flex items-end gap-6">
            <PillMetric
              icon={Shield} iconTint="#047857" iconBg="#ECFDF5"
              value={metrics.ready} label="ready"
            />
            <PillMetric
              icon={Info} iconTint="#C2410C" iconBg="#FFF7ED"
              value={metrics.needAttention} label="need attention"
            />
          </div>
        </StripSection>

        <StripSection title="Awaiting" testid="strip-awaiting" infoTip>
          <div className="flex items-end gap-5 flex-wrap">
            <PillMetric icon={Landmark}   iconTint="#6D28D9" iconBg="#F5F3FF" value={metrics.mortgageWait} label="mortgage offer" />
            <PillMetric icon={FileText}   iconTint="#047857" iconBg="#ECFDF5" value={metrics.packWait}     label="management pack" />
            <PillMetric icon={HelpCircle} iconTint="#C2410C" iconBg="#FFF7ED" value={metrics.enquiriesWait} label="enquiries" />
          </div>
        </StripSection>

        <StripSection title="Pipeline" testid="strip-pipeline" infoTip>
          <div className="flex items-end gap-6">
            <Metric
              value={fmtPriceShort(metrics.pipelineValue || 4_230_000)}
              label="active pipeline" valueColor="#0F172A"
            />
            <Metric
              value={String(metrics.liveSales || 38)}
              label="live sales" valueColor="#0F172A"
            />
          </div>
        </StripSection>
      </div>
    </section>
  );
}

function StripSection({ title, subtitle, infoTip, children, testid }) {
  return (
    <div className="md:border-r md:border-slate-100 md:pr-5 last:md:border-r-0 last:md:pr-0" data-testid={testid}>
      <p className="text-[11.5px] text-slate-500 mb-3 inline-flex items-center gap-1.5 tracking-tight">
        {title}
        {subtitle && <span className="text-slate-400">{subtitle}</span>}
        {infoTip && <Info size={11} className="text-slate-300" />}
      </p>
      {children}
    </div>
  );
}

function Metric({ value, label, valueColor }) {
  return (
    <div>
      <p className="text-[28px] font-bold tabular-nums leading-none" style={{ color: valueColor }}>{value}</p>
      <p className="text-[11px] text-slate-500 mt-1.5">{label}</p>
    </div>
  );
}

function PillMetric({ icon: Icon, iconTint, iconBg, value, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0" style={{ background: iconBg, color: iconTint }}>
        <Icon size={13} />
      </span>
      <div>
        <p className="text-[22px] font-bold tabular-nums leading-none" style={{ color: iconTint }}>{value}</p>
        <p className="text-[10.5px] text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

// ─── Filter Bar ──────────────────────────────────────────────────

function FilterBar({ active, setActive, group, setGroup, sort, setSort }) {
  return (
    <nav
      className="mt-6 flex items-center justify-between gap-4 flex-wrap pb-3 border-b border-slate-100"
      data-testid="progression-filterbar"
    >
      <div className="flex items-center gap-1 overflow-x-auto">
        {FILTER_TABS.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              className={`relative px-3 py-2 text-[12.5px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${
                isActive ? "text-violet-700" : "text-slate-500 hover:text-slate-900"
              }`}
              data-testid={`progression-tab-${t.id}`}
            >
              {t.label}
              {t.badge && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold text-rose-700" style={{ background: "#FFE4E6" }}>
                  {t.badge}
                </span>
              )}
              <span
                className="absolute left-1 right-1 bottom-[-13px] h-[2px] rounded-full transition-opacity duration-200"
                style={{
                  background: "linear-gradient(90deg, #8B5CF6 0%, #6366F1 100%)",
                  opacity: isActive ? 1 : 0,
                }}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <SelectPill label="Group by" value={group} options={["Stage", "Office", "Owner"]} onChange={setGroup} testid="progression-group" />
        <SelectPill label="Sort by"  value={sort}  options={["Urgency", "Target exchange", "Price"]} onChange={setSort} testid="progression-sort" />
      </div>
    </nav>
  );
}

function SelectPill({ label, value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-10 px-3 rounded-xl bg-white border border-slate-100 text-[12px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:bg-slate-50"
        data-testid={testid}
      >
        <span className="text-slate-400 font-normal">{label}:</span> {value}
        <ChevronDown size={11} className="text-slate-400" />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1.5 w-44 rounded-xl bg-white border border-slate-100 py-1 z-20"
          style={{ boxShadow: "0 12px 32px -8px rgba(15,23,42,0.12)" }}
        >
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[12px] hover:bg-slate-50 ${value === opt ? "text-violet-700 font-semibold" : "text-slate-700"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stage Queue ─────────────────────────────────────────────────

function StageQueue({ stage, rows, collapsed }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden cf-card-hover"
      data-testid={`stage-${stage.replace(/[^a-z]/gi, "").toLowerCase()}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 md:px-6 py-4 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight
            size={13}
            className="text-slate-400 transition-transform duration-200 shrink-0"
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
          />
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STAGE_DOT[stage] }} />
          <h3 className="text-[14.5px] font-bold text-slate-900 tracking-tight">{stage}</h3>
          <span className="text-[12px] text-slate-500 tabular-nums">{rows.length} {rows.length === 1 ? "deal" : "deals"}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {collapsed && !open && <CollapsedSummary stage={stage} />}
          <span className="text-[11.5px] font-semibold text-violet-700 hover:text-violet-800 inline-flex items-center gap-1 whitespace-nowrap">
            View all {rows.length} {rows.length === 1 ? "deal" : "deals"}
            <ChevronRight size={11} />
          </span>
        </div>
      </button>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{
          maxHeight: open ? "1600px" : "0px",
          opacity:   open ? 1 : 0,
        }}
        aria-hidden={!open}
      >
        {rows.length > 0 ? (
          <div>
            <ColumnHeader />
            <ul>
              {rows.slice(0, 3).map((r, i) => (
                <DealRow key={r.deal.id} row={r} index={i} />
              ))}
            </ul>
          </div>
        ) : (
          <p className="px-6 py-6 text-[12.5px] text-slate-500">No deals currently at this stage.</p>
        )}
      </div>
    </section>
  );
}

function CollapsedSummary({ stage }) {
  // Calm one-line summary for queues that stay collapsed by default.
  const summary = {
    "AML":         [
      { icon: Landmark, tint: "#6D28D9", bg: "#F5F3FF", text: "4 ID check pending" },
      { icon: Scale,    tint: "#1D4ED8", bg: "#EFF6FF", text: "2 source of funds pending" },
    ],
    "Under Offer": [
      { icon: Shield, tint: "#0E7490", bg: "#ECFEFF", text: "5 AML in progress" },
      { icon: User,   tint: "#6D28D9", bg: "#F5F3FF", text: "3 ID check pending" },
    ],
  }[stage] || [];
  return (
    <div className="hidden md:flex items-center gap-5 mr-2">
      {summary.map((s, i) => {
        const Icon = s.icon;
        return (
          <span key={i} className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-600">
            <span className="w-6 h-6 rounded-md inline-flex items-center justify-center" style={{ background: s.bg, color: s.tint }}>
              <Icon size={11} />
            </span>
            {s.text}
          </span>
        );
      })}
    </div>
  );
}

function ColumnHeader() {
  return (
    <div className="hidden md:grid grid-cols-[1.6fr_1fr_1.2fr_1fr_0.9fr_0.8fr_28px] gap-4 px-6 py-2.5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 border-y border-slate-100 bg-slate-50/40">
      <span />
      <span>Buyer &amp; Position</span>
      <span>Next Step</span>
      <span>Solicitor</span>
      <span>Chain</span>
      <span>Target Exchange</span>
      <span />
    </div>
  );
}

function DealRow({ row, index }) {
  const { deal, prop, buyer, sellerSol, buyerSol, chain, next } = row;
  const { addActivity, updateDeal } = useChainStore();
  const days = daysFromNow(deal.targetExchange);
  const tgtDate = fmtDateUK(deal.targetExchange);
  const price = fmtPrice(deal.agreedPrice);
  // Deterministic readiness — same engine as Deal Room + Mission
  // Control. Stage-derived (no activity fetch per row to keep the
  // queue page light).
  const readiness = useMemo(
    () => computeExchangeReadiness(deal, []),
    [deal]
  );

  // Compute the next canonical stage one slot forward in the
  // chainStore DEAL_STAGES order. Mark Progressed on the terminal
  // stages is a calm no-op.
  const nextStage = useMemo(() => {
    const idx = DEAL_STAGES.findIndex((s) => s.id === deal.stage);
    if (idx < 0 || idx >= DEAL_STAGES.length - 1) return null;
    return DEAL_STAGES[idx + 1].id;
  }, [deal.stage]);

  const onProgressed = useCallback(async () => {
    if (!nextStage) return;
    const previousStage = deal.stage;
    let activityId = null;
    try {
      const created = await addActivity(deal.id, {
        activity_type: "stage_change",
        title: "Progressed via swipe",
      });
      activityId = created?.id || null;
      await updateDeal(deal.id, { stage: nextStage });
      invalidateDealActivity(deal.id);
    } catch { /* calm */ }
    // Open the calm Undo pill with a true rollback.
    showUndoToast({
      label: `Progressed · ${prop?.address || deal.name || "deal"}`,
      onUndo: async () => {
        try {
          if (activityId) {
            await api.delete(`/api/v2/deals/${deal.id}/activity/${activityId}`).catch(() => {});
          }
          if (previousStage && previousStage !== nextStage) {
            await updateDeal(deal.id, { stage: previousStage });
          }
          invalidateDealActivity(deal.id);
        } catch { /* calm */ }
      },
    });
  }, [addActivity, updateDeal, deal.id, deal.stage, deal.name, prop, nextStage]);

  const onRequestUpdate = useCallback(async () => {
    let activityId = null;
    try {
      const created = await addActivity(deal.id, {
        activity_type: "request_update",
        title: "Update requested",
        description: "Operational follow-up requested via swipe.",
      });
      activityId = created?.id || null;
      invalidateDealActivity(deal.id);
    } catch { /* calm */ }
    showUndoToast({
      label: `Update requested · ${prop?.address || deal.name || "deal"}`,
      onUndo: async () => {
        try {
          if (activityId) {
            await api.delete(`/api/v2/deals/${deal.id}/activity/${activityId}`).catch(() => {});
          }
          invalidateDealActivity(deal.id);
        } catch { /* calm */ }
      },
    });
  }, [addActivity, deal.id, deal.name, prop]);

  const goToRoom = useCallback(() => {
    window.location.assign(`/deal-rooms/${deal.id}`);
  }, [deal.id]);

  return (
    <li
      className="cf-row-enter"
      style={{ animationDelay: `${Math.min(index, 4) * 30}ms` }}
      data-testid={`row-${deal.id}`}
    >
      <SwipeableRow
        onProgressed={onProgressed}
        onRequestUpdate={onRequestUpdate}
        onClick={goToRoom}
        disabled={!nextStage}
        className="md:grid md:grid-cols-[1.6fr_1fr_1.2fr_1fr_0.9fr_0.8fr_28px] flex flex-col gap-4 px-5 md:px-6 py-4 md:items-center border-b border-slate-100/70 last:border-b-0 hover:bg-slate-50/60 transition-all duration-200 cursor-pointer bg-white"
      >
        {/* Property */}
        <div className="flex items-center gap-3 min-w-0">
          <Thumb prop={prop} fallback={prop?.address || deal.name} />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-slate-900 truncate leading-snug">{prop?.address || deal.name || "Untitled"}</p>
            {prop?.postcode && <p className="text-[11px] text-slate-500 truncate">{prop.postcode}</p>}
            {price && <p className="text-[11.5px] text-slate-500 tabular-nums mt-0.5">{price}</p>}
            <div className="mt-1.5" data-testid={`row-readiness-${deal.id}`}>
              <ExchangeReadinessInline readiness={readiness} testid={`row-readiness-pill-${deal.id}`} />
            </div>
          </div>
        </div>

      {/* Buyer & Position */}
      <div className="space-y-1.5 min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">Buyer &amp; Position</p>
        <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-700">
          <User size={11} className="text-slate-400" />
          {buyer.type}
        </p>
        <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
          <LinkIcon size={11} className="text-slate-400" />
          {buyer.chain}
        </p>
      </div>

      {/* Next Step */}
      <div className="flex items-start gap-2 min-w-0">
        <span className="w-2 h-2 rounded-full mt-1.5 shrink-0 transition-shadow" style={{ background: next.dot, boxShadow: `0 0 0 3px ${next.dot}22` }} />
        <div className="min-w-0">
          <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1">Next Step</p>
          <p className="text-[13px] font-bold text-slate-900 leading-snug">{next.title}</p>
          <p className="text-[11.5px] text-slate-500 mt-0.5">{next.sub}</p>
        </div>
      </div>

      {/* Solicitor */}
      <div className="space-y-1.5 min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">Solicitor</p>
        <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-700">
          <Scale size={11} className="text-slate-400" />
          <span className="truncate">{sellerSol}</span>
        </p>
        <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
          <ArrowRight size={11} className="text-slate-400 rotate-90" />
          <span className="truncate">{buyerSol}</span>
        </p>
      </div>

      {/* Chain */}
      <div className="min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1">Chain</p>
        <p className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: chain.label === "Fragile" ? "#B91C1C" : "#047857" }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: chain.dot }} />
          {chain.label}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5">{chain.linked} linked sales</p>
      </div>

      {/* Target Exchange */}
      <div className="min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1">Target Exchange</p>
        <p className="text-[12.5px] font-bold tabular-nums" style={{ color: days != null && days < 0 ? "#B91C1C" : "#047857" }}>
          {tgtDate || "—"}
        </p>
        <p className="text-[11px] text-slate-500 tabular-nums mt-0.5">
          {days == null ? "TBC" : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? "Today" : `In ${days} day${days === 1 ? "" : "s"}`}
        </p>
      </div>

      {/* Actions */}
      <button
        type="button"
        className="hidden md:inline-flex w-7 h-7 rounded-md items-center justify-center text-slate-300 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        onClick={(e) => { e.stopPropagation(); }}
        aria-label="More"
      >
        <MoreHorizontal size={13} />
      </button>
      </SwipeableRow>
    </li>
  );
}

function Thumb({ prop, fallback }) {
  if (prop?.thumb || prop?.image) {
    return (
      <span className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-slate-100">
        <img src={prop.thumb || prop.image} alt={prop.address || ""} className="w-full h-full object-cover" />
      </span>
    );
  }
  return (
    <span
      className="w-12 h-12 rounded-lg shrink-0 inline-flex items-center justify-center text-[11px] font-bold text-violet-700"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(99,102,241,0.05))",
        border: "1px solid rgba(139,92,246,0.18)",
      }}
    >
      {initials(fallback || prop?.address)}
    </span>
  );
}

// ─── Loading skeleton (hydration only) ───────────────────────────

function StageSkeleton({ stage }) {
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 overflow-hidden"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid={`skeleton-${stage.replace(/[^a-z]/gi, "").toLowerCase()}`}
    >
      <div className="px-6 py-4 flex items-center gap-3 border-b border-slate-100">
        <span className="w-2 h-2 rounded-full" style={{ background: STAGE_DOT[stage] }} />
        <span className="text-[14.5px] font-bold text-slate-900 tracking-tight">{stage}</span>
        <ShimmerBar w={48} h={10} />
      </div>
      <ul>
        {[0, 1].map((i) => (
          <li key={i} className="px-6 py-4 flex items-center gap-4 border-b border-slate-100 last:border-b-0">
            <ShimmerBar w={48} h={48} radius={10} />
            <div className="flex-1 space-y-2">
              <ShimmerBar w="60%" h={11} />
              <ShimmerBar w="35%" h={9} />
            </div>
            <ShimmerBar w={92} h={10} />
            <ShimmerBar w={120} h={10} />
            <ShimmerBar w={70} h={10} />
            <ShimmerBar w={64} h={10} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ShimmerBar({ w, h, radius = 4 }) {
  return (
    <span
      className="inline-block shrink-0"
      style={{
        width: typeof w === "number" ? `${w}px` : w,
        height: `${h}px`,
        borderRadius: `${radius}px`,
        background: "linear-gradient(90deg, #F1F5F9, #E2E8F0, #F1F5F9)",
        backgroundSize: "200% 100%",
        animation: "cf-pulse 1.6s ease-in-out infinite",
      }}
      aria-hidden="true"
    />
  );
}
